import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useContext, useEffect, useCallback, useRef } from 'react';

import { Message } from 'components/ChatBV/types';
import { OpinionResult } from 'routes/Dashboard/CourtCases';
import { useCourtCasesApi } from 'routes/Dashboard/CourtCases/api';

interface UseCourtOpinionLoaderProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

export const useCourtOpinionLoader = ({
  messages,
  setMessages,
}: UseCourtOpinionLoaderProps) => {
  const { logError } = useContext(AppConfig);
  const { getCluster } = useCourtCasesApi();
  const loadingRef = useRef<Set<number>>(new Set());

  const loadCourtOpinionForMessage = useCallback(
    async (message: Message, index: number) => {
      const clusterId = message.courtOpinionClusterId as number | undefined;

      // Skip if already loading or doesn't meet conditions
      if (
        !clusterId ||
        loadingRef.current.has(clusterId) ||
        message.courtOpinionData
      ) {
        return;
      }

      // Mark as loading
      loadingRef.current.add(clusterId);

      // Helper function to safely update message
      const updateMessage = (updates: Partial<Message>) => {
        setMessages((prevMessages: Message[]) => {
          const updatedMessages = [...prevMessages];
          const currentMessage = updatedMessages[index];
          if (
            currentMessage &&
            currentMessage.courtOpinionClusterId === clusterId
          ) {
            updatedMessages[index] = {
              ...currentMessage,
              ...updates,
            };
            // Keep courtOpinionClusterId for context restoration
          }
          return updatedMessages;
        });
      };

      try {
        const clusterData = await getCluster(clusterId.toString());

        if (clusterData) {
          // Convert cluster data to OpinionResult format
          // Note: clusterData is a CourtCaseCluster, we need to map it to OpinionResult
          const courtOpinionData: OpinionResult = {
            cluster_id: clusterId,
            caseName: clusterData.case_name || '',
            caseNameFull: clusterData.case_name_full || '',
            citation: clusterData.citations || [],
            court: '', // Court name is not directly available in cluster data
            docketNumber: clusterData.docket_id?.toString() || '',
            dateFiled: clusterData.date_filed || '',
            status: '',
            suitNature: clusterData.nature_of_suit || '',
            judge:
              typeof clusterData.judges === 'string' ? clusterData.judges : '',
            opinions: [],
            syllabus: clusterData.syllabus || '',
            // Add required fields with defaults
            absolute_url: clusterData.absolute_url || '',
            attorney:
              typeof clusterData.attorneys === 'string'
                ? clusterData.attorneys
                : '',
            citeCount: clusterData.citation_count || 0,
            court_citation_string: '',
            docket_id: clusterData.docket_id || 0,
            // Add other required fields
            court_id: '',
            dateArgued: null,
            dateReargued: null,
            dateReargumentDenied: null,
            lexisCite: '',
            neutralCite: '',
            non_participating_judge_ids: [],
            panel_ids: [],
            panel_names: [],
            posture: clusterData.posture || '',
            procedural_history: clusterData.procedural_history || '',
            scdb_id: clusterData.scdb_id || '',
            sibling_ids: [],
            source: clusterData.source || '',
            meta: {
              timestamp: clusterData.date_created || '',
              date_created: clusterData.date_created || '',
              score: {
                bm25: 0,
              },
            },
          };

          updateMessage({
            courtOpinionData,
          });
        } else {
          // If cluster not found, remove the clusterId marker to prevent retries
          updateMessage({
            courtOpinionClusterId: undefined,
          });
        }
      } catch (error) {
        logError('Error loading court opinion for message', error as Error);
        // Remove the clusterId marker on error to prevent retries
        updateMessage({
          courtOpinionClusterId: undefined,
        });
      } finally {
        // Remove from loading set
        loadingRef.current.delete(clusterId);
      }
    },
    [setMessages, logError, getCluster]
  );

  useEffect(() => {
    // Find all messages that need court opinion loading
    messages.forEach((message, index) => {
      const clusterId = message.courtOpinionClusterId;
      // Load if we have clusterId and no data yet, regardless of content
      // (content might be set to COURT_OPINION_PAGE during restoration)
      if (clusterId && !message.courtOpinionData) {
        loadCourtOpinionForMessage(message, index);
      }
    });
  }, [messages, loadCourtOpinionForMessage]);
};
